import { FunctionComponent, useState, useMemo } from "react";
import { SearchBar } from "../inputs";
import TableHeaders from "./TableHeaders";
import TableRows from "./TableRows";

interface ITableProps {
  columns: string[];
  rows: any[][];
}

const Table: FunctionComponent<ITableProps> = ({ columns, rows }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null
  );

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      // toggle direction
      setSortDirection(
        sortDirection === "asc"
          ? "desc"
          : sortDirection === "desc"
          ? null
          : "asc"
      );
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return rows;

    const columnIndex = columns.indexOf(sortColumn);
    const sorted = [...rows].sort((a, b) => {
      const valA = a[columnIndex];
      const valB = b[columnIndex];

      if (!isNaN(valA) && !isNaN(valB)) {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      }

      return sortDirection === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    return sorted;
  }, [rows, sortColumn, sortDirection, columns]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return sortedRows;
    return sortedRows.filter((row) =>
      row.some(
        (cell) =>
          typeof cell === "string" &&
          cell.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, sortedRows]);

  return (
    <div className="p-4 bg-gradient-to-br from-white to-primary/20 rounded-xl shadow-lg backdrop-blur-sm">
      <SearchBar onChange={(term: string) => setSearchTerm(term)} />

      <div className="overflow-x-auto mt-4">
        <table className="w-full border-collapse rounded-lg overflow-hidden text-left shadow-md">
          <TableHeaders
            columns={columns}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
          />

          {filteredRows.length ? (
            <TableRows rows={filteredRows} />
          ) : (
            <tbody>
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center p-6 text-gray-400"
                >
                  No matching results
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default Table;
